import { useCallback, useState } from 'react';
import { Pressable, View } from 'react-native';

import { HStack, Text, VStack, theme } from '@squire/ui';

import { AfterpayBadgeIcon } from '@/components/_icons/AfterpayBadgeIcon';
import { BnplIcon } from '@/components/_icons/BnplIcon';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { BnplInstallmentMessage } from '../BnplInstallmentMessage';
import { styles } from './styles';
import type { BnplPaymentMethodRowProps } from './types';

const BnplPaymentMethodRow = ({
  variant,
  selected,
  onPress,
  amountCents,
  currency,
  country,
  eligibleMethods,
  testID,
}: BnplPaymentMethodRowProps) => {
  const { t } = useScreenTranslations('ConfirmBookingBottomSheet');
  const [isMessageReady, setIsMessageReady] = useState(false);

  const handleMessageReady = useCallback(() => setIsMessageReady(true), []);

  const label = variant === 'pay-later' ? t('payLater') : t('afterpay');
  const shouldMountMessage = variant === 'pay-later' || selected;
  const isMessageVisible = shouldMountMessage && isMessageReady;

  styles.useVariants({ selected });

  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      accessibilityRole="radio"
      aria-checked={selected}
      accessibilityLabel={t('accessibility.paymentMethod', { method: label })}
      accessibilityHint={t('accessibility.paymentMethodHint', {
        method: label,
      })}
      style={styles.row}
    >
      <VStack space={theme.spacing[3]}>
        <HStack space={theme.spacing[3]} style={styles.labelRow}>
          <View style={styles.badge}>
            {variant === 'now-later' ? (
              <AfterpayBadgeIcon selected={selected} />
            ) : (
              <BnplIcon selected={selected} />
            )}
          </View>

          <Text
            variant="lStrong"
            color={selected ? 'textPrimaryInverse' : 'textPrimary'}
          >
            {label}
          </Text>
        </HStack>

        {isMessageVisible && (
          <View testID={`${testID}-divider`} style={styles.divider} />
        )}

        {shouldMountMessage && (
          <View style={isMessageVisible ? undefined : styles.messageHidden}>
            <BnplInstallmentMessage
              amountCents={amountCents}
              currency={currency}
              country={country}
              eligibleMethods={eligibleMethods}
              selected={selected}
              onReady={handleMessageReady}
            />
          </View>
        )}
      </VStack>
    </Pressable>
  );
};

BnplPaymentMethodRow.displayName = 'BnplPaymentMethodRow';
export { BnplPaymentMethodRow };
